import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetFancyProfitLossQuery, useGetFancyDropdownQuery } from '../services/sports/sportsApi';
import { useLazySearchUsersQuery } from '../services/auth/authApi';
import Loading from '../components/Loading';

const FancyProfitLossPage = () => {
  const navigate = useNavigate();
  const { matchId, matchName } = useParams();

  // Filter states
  const [selectedFancy, setSelectedFancy] = useState("");
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Search users API
  const [searchUsers, { isLoading: searchLoading }] = useLazySearchUsersQuery();

  // Fancy dropdown API
  const { data: fancyDropdownData } = useGetFancyDropdownQuery({
    eventId: matchId
  }, {
    skip: !matchId
  });

  // Call API with matchId and pagination
  const { data: apiData, isLoading, isFetching, error: apiError } = useGetFancyProfitLossQuery({
    eventId: matchId,
    fancyId: selectedFancy || undefined,
    search: searchQuery || undefined,
    page: currentPage,
    limit: itemsPerPage
  });

  // Extract data from API response
  const completedSessions = apiData?.data || [];
  const apiTotalPnl = apiData?.totalPnl || 0;
  const apiNetAmount = apiData?.summary?.netAmount ?? apiData?.netAmount ?? 0;
  const dropdownUsers = apiData?.dropdowns?.users || [];
  const dropdownFancies = apiData?.dropdowns?.fancies || [];
  const pagination = apiData?.pagination || {};
  const totalPages = pagination.totalPages || 1;

  // Use API data directly
  let filteredData = completedSessions;

  // Handle search input change
  const handleSearchInput = async (value) => {
    setSearchInput(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await searchUsers(value.trim()).unwrap();
        if (result?.success && result?.data) {
          setSearchResults(result.data);
          setShowDropdown(true);
        } else {
          setSearchResults([]);
          setShowDropdown(false);
        }
      } catch (err) {
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 300);
  };

  // Handle user selection from dropdown
  const handleSelectUser = (user) => {
    setSearchInput(user.userId || user.userName);
    setSearchQuery(user.userId || user.userName);
    setShowDropdown(false);
    setCurrentPage(1);
  };

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds} ${ampm}`;
  };

  // Calculate total P/L
  const totalPnL = filteredData.reduce(function(sum, item) {
    return sum + (item.pnl || 0);
  }, 0);

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full px-1">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-gray-600 px-5 py-2 flex items-center justify-between">
              <span className="text-xl text-white font-normal capitalize">
                Fancy Profit and Loss
              </span>
              <button 
                onClick={function() { navigate(-1); }}
                className="bg-blue-500 text-white px-4 py-1 hover:bg-blue-600"
                style={{ borderRadius: 0 }}
              >
                Back
              </button>
            </div>

            {/* Filters and Total P/L */}
            <div className="flex flex-col md:flex-row items-start md:items-center px-5 py-3 gap-4">
              {/* Fancy Select */}
              <div className="w-full md:w-auto">
                <select
                  value={selectedFancy}
                  onChange={function(e) { 
                    setSelectedFancy(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full md:w-auto px-3 py-2 border border-gray-300 focus:outline-none focus:border-blue-500"
                  style={{ minWidth: "280px", borderRadius: 0 }}
                >
                  <option value="">All Fancies</option>
                  {fancyDropdownData?.data?.map(function(fancy) {
                    return (
                      <option key={fancy.fancyId} value={fancy.fancyId}>
                        {fancy.fancyName}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* User Search */}
              <div className="relative w-full md:w-auto" ref={dropdownRef}>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => handleSearchInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && searchInput.trim()) {
                      setSearchQuery(searchInput);
                      setShowDropdown(false);
                      setCurrentPage(1);
                    }
                  }}
                  onFocus={() => {
                    if (searchInput.trim() && searchResults.length > 0) {
                      setShowDropdown(true);
                    }
                  }}
                  placeholder="Search by User ID or Name"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                  style={{ minWidth: "280px" }}
                />
                {/* Dropdown */}
                {showDropdown && searchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-auto">
                    {searchLoading ? (
                      <div className="px-3 py-2 text-gray-500">Searching...</div>
                    ) : (
                      searchResults.map((user, index) => (
                        <div
                          key={user.id || index}
                          onClick={() => handleSelectUser(user)}
                          className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0"
                        >
                          <div className="font-medium text-gray-900">{user.userName || user.userId}</div>
                          <div className="text-sm text-gray-500">{user.userId} {user.fullName ? `- ${user.fullName}` : ''}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={function() {
                  setSearchInput('');
                  setSearchQuery('');
                  setSearchResults([]);
                  setShowDropdown(false);
                  setCurrentPage(1);
                }}
                className="bg-gray-500 text-white px-6 py-2 hover:bg-gray-600 transition-colors w-full md:w-auto"
                style={{ borderRadius: 0 }}
              >
                Clear
              </button>

              {/* Net Amount Display */}
              <div className="flex items-center text-lg md:ml-auto">
                <span className="text-gray-700 font-medium">Total P/L:</span>
                <span className={`ml-2 font-bold ${apiNetAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {Number(apiNetAmount).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : apiError ? (
                <div className="p-4 text-red-500 text-center">
                  {apiError.message || "Failed to load data"}
                </div>
              ) : (
                <table className="w-full border-collapse text-sm border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 border border-gray-300 text-left font-medium">User Id</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">Session Name</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">Back Lay</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">Value</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">Bet Size</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">Bet Amount</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">Result</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">P/L</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="p-8 text-center text-gray-500 bg-gray-50">
                          <div className="text-gray-400">No Data</div>
                        </td>
                      </tr>
                    ) : (
                      filteredData.map(function(item, index) {
                        const isYes = ["yes", "lagai", "back"].includes((item.backLay || '').toLowerCase());
                        const isNo = ["no", "khai", "lay"].includes((item.backLay || '').toLowerCase());
                        const rowBgColor = isYes ? "rgba(59,130,246,0.20)" : isNo ? "rgba(236,72,153,0.20)" : undefined;

                        return (
                          <tr key={index} style={rowBgColor ? { backgroundColor: rowBgColor } : undefined}>
                            <td className="p-2 border border-gray-300">
                              <div>{item.fullName || item.username || '-'}</div>
                              {item.userId && <div className="text-xs text-gray-500">{item.userId}</div>}
                            </td>
                            <td className="p-2 border border-gray-300">{item.fancyName || "-"}</td>
                            <td className="p-2 border border-gray-300">{item.backLay || "-"}</td>
                            <td className="p-2 border border-gray-300">{item.value || "-"}</td>
                            <td className="p-2 border border-gray-300">{item.volume || "-"}</td>
                            <td className="p-2 border border-gray-300">{item.stake || "-"}</td>
                            <td className="p-2 border border-gray-300">{item.result || "-"}</td>
                            <td className="p-2 border border-gray-300">{item.pnl || "0"}</td>
                            <td className="p-2 border border-gray-300">{formatDate(item.date)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls */}
            {completedSessions.length > 0 && (
              <div className="flex flex-col md:flex-row justify-between items-center p-3 border-t gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Items per page:</span>
                  <select
                    value={itemsPerPage}
                    onChange={function(e) {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 border border-gray-300 text-sm focus:outline-none focus:border-blue-500"
                    style={{ borderRadius: 0 }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>

                <div className="text-sm text-gray-600">
                  Total: {apiData?.totalBets || 0} bets
                </div>

                <div className="flex items-center gap-2">
                  <button
                    disabled={currentPage === 1}
                    onClick={function() { setCurrentPage(currentPage - 1); }}
                    className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    style={{ borderRadius: 0 }}
                  >
                    Prev
                  </button>
                  <span className="text-sm text-gray-600 whitespace-nowrap">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    disabled={currentPage >= totalPages}
                    onClick={function() { setCurrentPage(currentPage + 1); }}
                    className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    style={{ borderRadius: 0 }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FancyProfitLossPage;
